/**
 * "Edge vs a same-size market basket" (lane OBS-REL), the first block of each fly's
 * Benchmarks panel. Both flies run core-only with the tilt off, so their dollar
 * curves track the market almost exactly; this block subtracts a basket of the same
 * size so any edge — or drag — is visible instead of buried in market moves:
 *
 * - a headline: edge since genesis, the last 30 days, the tracking error (and the
 *   excess over T-bills for scale), plus a "the tilt is OFF" badge;
 * - a chart of the cumulative excess over the basket and over T-bills, zero-lined;
 * - a decomposition strip of the total return: core (beta), tilt, the T-bill sleeve
 *   (rf, not edge), costs (fees + spread) and the tariff, summing to the total.
 *
 * Binds to `refs.benchmarks.market_relative` only; the chart chunk is lazily
 * imported like `BenchmarkChart`. An explicit empty state covers documents published
 * before the block existed and histories shorter than two counted days.
 */

import type { BenchmarksDocument, MarketRelativeDocument } from "../types/incremental";
import type { ExcessChart } from "../charts/ExcessChart";
import {
  EXCESS_SERIES_COLORS,
  EXCESS_SERIES_DASHED,
  EXCESS_SERIES_LABELS,
  EXCESS_SERIES_ORDER,
  EXCESS_SERIES_TOOLTIPS,
  buildMarketRelativeState,
  decompositionItems,
  decompositionTotal,
  headlineItems,
  tiltBadge,
  trackingSentence,
  type ExcessSeriesName,
} from "./marketRelativeView";
import { attachTooltip } from "../ui/tooltip";
import { formatSignedPercent, toneClass } from "../format/number";

export class MarketRelativeBlock {
  readonly element: HTMLDivElement;
  private chart: ExcessChart | null = null;
  private chartWrap: HTMLDivElement | null = null;
  private block: MarketRelativeDocument | null = null;
  private readonly legendValues = new Map<ExcessSeriesName, HTMLSpanElement>();
  private disposed = false;

  constructor(doc: BenchmarksDocument) {
    this.element = document.createElement("div");
    this.element.className = "obs-rel";
    this.build(doc);
  }

  private build(doc: BenchmarksDocument): void {
    const state = buildMarketRelativeState(doc);
    const head = document.createElement("div");
    head.className = "obs-rel-head";
    const title = document.createElement("span");
    title.className = "obs-rel-title";
    title.textContent = "Edge vs a same-size market basket";
    attachTooltip(
      title,
      "The fly minus an equal-weight basket of its own universe, scaled every day to the fly's " +
        "actual held gross (the rest in T-bills, same tariff) — the market's move removed, so " +
        "what is left is what the fly did differently.",
    );
    head.appendChild(title);
    const badge = tiltBadge(state.kind === "ready" ? state.block.tilt_active : state.tiltActive);
    const pill = document.createElement("span");
    pill.className = `obs-rel-tilt ${badge.className}`;
    pill.textContent = badge.text;
    attachTooltip(pill, badge.tooltip);
    head.appendChild(pill);
    this.element.appendChild(head);

    if (state.kind === "empty") {
      const empty = document.createElement("div");
      empty.className = "obs-rel-empty";
      empty.textContent = state.message;
      this.element.appendChild(empty);
      return;
    }
    const block = state.block;
    this.block = block;

    const tiles = document.createElement("div");
    tiles.className = "obs-rel-headline";
    for (const item of headlineItems(block)) {
      const tile = document.createElement("div");
      tile.className = "obs-rel-stat";
      const label = document.createElement("span");
      label.className = "obs-stat-label";
      label.textContent = item.label;
      const value = document.createElement("span");
      value.className = `obs-rel-stat-value ${toneClass(item.value.tone)}`;
      value.textContent = item.value.text;
      tile.appendChild(label);
      tile.appendChild(value);
      attachTooltip(tile, item.tooltip);
      tiles.appendChild(tile);
    }
    this.element.appendChild(tiles);

    const sentence = document.createElement("div");
    sentence.className = "obs-panel-hint obs-rel-sentence";
    sentence.textContent = trackingSentence(block);
    this.element.appendChild(sentence);

    this.element.appendChild(this.buildLegend(block));
    const chartWrap = document.createElement("div");
    chartWrap.className = "obs-rel-chart-wrap";
    this.chartWrap = chartWrap;
    this.element.appendChild(chartWrap);
    this.element.appendChild(this.buildDecomposition(block));
  }

  private buildLegend(block: MarketRelativeDocument): HTMLDivElement {
    const legend = document.createElement("div");
    legend.className = "obs-bench-legend";
    const finals: Record<ExcessSeriesName, number | null> = {
      excess_vs_basket: block.headline?.excess_since_genesis ?? null,
      excess_vs_tbills: block.headline?.excess_vs_tbills_since_genesis ?? null,
    };
    for (const name of EXCESS_SERIES_ORDER) {
      const item = document.createElement("span");
      item.className = "obs-bench-legend-item";
      const swatch = document.createElement("span");
      swatch.className = "obs-bench-legend-swatch";
      if (EXCESS_SERIES_DASHED.has(name)) swatch.classList.add("obs-bench-legend-swatch-dashed");
      swatch.style.setProperty("--obs-bench-color", EXCESS_SERIES_COLORS[name]);
      item.appendChild(swatch);
      item.appendChild(document.createTextNode(EXCESS_SERIES_LABELS[name]));
      const value = document.createElement("span");
      value.className = "obs-bench-legend-value";
      value.textContent = formatSignedPercent(finals[name]).text;
      value.dataset.final = value.textContent;
      this.legendValues.set(name, value);
      item.appendChild(value);
      attachTooltip(item, EXCESS_SERIES_TOOLTIPS[name]);
      legend.appendChild(item);
    }
    return legend;
  }

  private buildDecomposition(block: MarketRelativeDocument): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "obs-rel-decomp";
    const caption = document.createElement("div");
    caption.className = "obs-stat-label";
    caption.textContent = "Where the return came from (since genesis, % of opening capital)";
    wrap.appendChild(caption);
    for (const item of decompositionItems(block)) {
      const row = document.createElement("div");
      row.className = "obs-rel-decomp-row";
      row.dataset.key = item.key;
      const label = document.createElement("span");
      label.className = "obs-rel-decomp-label";
      label.textContent = item.label;
      if (item.tag) {
        const tag = document.createElement("span");
        tag.className = "obs-rel-tag";
        tag.textContent = item.tag;
        label.appendChild(tag);
      }
      const bar = document.createElement("span");
      bar.className = "obs-rel-decomp-bar";
      const fill = document.createElement("span");
      fill.className =
        "obs-rel-decomp-fill " +
        (item.raw !== null && item.raw < 0 ? "obs-rel-decomp-neg" : "obs-rel-decomp-pos");
      if (item.key === "rf") fill.classList.add("obs-rel-decomp-rf");
      fill.style.width = `${(item.share * 50).toFixed(2)}%`;
      bar.appendChild(fill);
      const value = document.createElement("span");
      value.className = `obs-rel-decomp-value ${toneClass(item.value.tone)}`;
      value.textContent = item.value.text;
      row.appendChild(label);
      row.appendChild(bar);
      row.appendChild(value);
      attachTooltip(row, item.note);
      wrap.appendChild(row);
    }
    const total = decompositionTotal(block);
    const totalRow = document.createElement("div");
    totalRow.className = "obs-rel-decomp-total";
    totalRow.textContent = total.consistent ? total.text : `${total.text} (parts do not sum!)`;
    wrap.appendChild(totalRow);
    return wrap;
  }

  /** Mounts the lazily-imported chart; a no-op in the empty state. */
  async mountChart(): Promise<void> {
    const block = this.block;
    const wrap = this.chartWrap;
    if (!block || !wrap) return;
    const { ExcessChart } = await import("../charts/ExcessChart");
    if (this.disposed || this.chartWrap !== wrap) return;
    this.chart = new ExcessChart(wrap, (values) => {
      for (const [name, el] of this.legendValues) {
        el.textContent = values
          ? formatSignedPercent(values[name] ?? null).text
          : (el.dataset.final ?? "–");
      }
    });
    this.chart.setBlock(block);
  }

  dispose(): void {
    this.disposed = true;
    this.chart?.dispose();
    this.chart = null;
  }
}
